import { Check } from "iconoir-react";
import type { CSSProperties } from "react";

import { liveHomeCopy as enLiveHomeCopy } from "@/content/pages/en/live-home";

import { CardLede } from "./card-lede";
import type { TermsCopy } from "./types";
import type { TermsMotionState } from "./use-terms-card-motion";

export const GUARANTEES_DURATION_MS = 650;
export const GLASS_END = 0.62;
export const GRAY_GLOW_START = 0.15;
export const GRAY_GLOW_END = 450 / GUARANTEES_DURATION_MS;
export const BLUE_GLOW_START = 0.18;
export const BLUE_GLOW_END = 500 / GUARANTEES_DURATION_MS;
export const CHECK_START = 0.31;
export const CHECK_END = 500 / GUARANTEES_DURATION_MS;

export interface GuaranteesMockProps {
  copy?: TermsCopy;
  motionState?: TermsMotionState;
  phase?: number;
}

function segmentProgress(phase: number, start: number, end = 1): number {
  if (phase <= start) return 0;
  if (phase >= end) return 1;
  return (phase - start) / (end - start);
}

function resolveGuaranteesPhase(
  motionState: TermsMotionState = "complete",
  phase?: number
): number {
  if (phase !== undefined) return phase;
  if (motionState === "idle") return 0;
  return 1;
}

function shouldApplyMotionStyles(
  motionState: TermsMotionState,
  phase?: number
): boolean {
  return phase !== undefined || motionState !== "complete";
}

export function GuaranteesMock({
  copy = enLiveHomeCopy.terms,
  motionState = "complete",
  phase,
}: GuaranteesMockProps = {}) {
  const resolvedPhase = resolveGuaranteesPhase(motionState, phase);
  const applyMotionStyles = shouldApplyMotionStyles(motionState, phase);
  const glassProgress = segmentProgress(resolvedPhase, 0, GLASS_END);
  const grayGlowProgress = segmentProgress(
    resolvedPhase,
    GRAY_GLOW_START,
    GRAY_GLOW_END
  );
  const blueGlowProgress = segmentProgress(
    resolvedPhase,
    BLUE_GLOW_START,
    BLUE_GLOW_END
  );
  const checkProgress = segmentProgress(resolvedPhase, CHECK_START, CHECK_END);
  const checkShowsOverlay = applyMotionStyles && checkProgress < 1;

  const glassStyle: CSSProperties | undefined = applyMotionStyles
    ? {
        clipPath: `inset(0 ${(1 - glassProgress) * 100}% 0 0)`,
        transform: `translateX(${-6 * (1 - glassProgress)}%)`,
        opacity: glassProgress,
      }
    : undefined;

  const grayGlowStyle: CSSProperties | undefined = applyMotionStyles
    ? { opacity: grayGlowProgress }
    : undefined;

  const blueGlowStyle: CSSProperties | undefined = applyMotionStyles
    ? { opacity: blueGlowProgress }
    : undefined;

  const blueGlowScaleStyle: CSSProperties | undefined = applyMotionStyles
    ? {
        transform: `scale(${0.92 + 0.08 * blueGlowProgress})`,
        transformOrigin: "left center",
      }
    : undefined;

  const checkTileStyle: CSSProperties | undefined = applyMotionStyles
    ? {
        opacity: checkProgress,
        transform: `scale(${0.9 + 0.1 * checkProgress})`,
      }
    : undefined;

  return (
    <>
      {/* Faded "No personal liability" row (Figma 6142:29494) — behind, lower. */}
      <div className="bg-grey-50 absolute top-[226.4px] left-[35px] h-[73.36px] w-[438.7px] overflow-hidden rounded-[11.2px] opacity-44 lg:top-[215.77px] lg:left-[43.57px]">
        <span className="bg-grey-0 absolute top-[8.4px] left-[8.16px] flex size-[56.95px] items-center justify-center rounded-[3.73px] shadow-[-10.27px_8.4px_29.87px_0px_rgba(0,0,0,0.05)]">
          <span className="bg-grey-100 flex size-[18px] items-center justify-center rounded-full">
            <Check className="size-3 text-white" strokeWidth={3} />
          </span>
        </span>
        <span className="text-grey-950 absolute top-[26.33px] left-[80px] text-[13px] leading-[1.4] tracking-[-0.02em] opacity-30">
          {copy.mockNoPersonalLiabilityLabel}
        </span>
      </div>

      {/* Gray glow (Figma 6142:29476) — the bar's soft drop shadow. */}
      <span
        aria-hidden
        data-testid="guarantees-glow-gray"
        className="pointer-events-none absolute top-[207px] right-[-36px] bottom-0 left-[27px] rounded-[16px] blur-[8px] lg:top-[196px] lg:left-[36px]"
        style={{
          background:
            "linear-gradient(180deg, rgba(0,0,0,0.22) 0%, rgba(0,0,0,0.06) 42%, rgba(0,0,0,0) 100%)",
          ...grayGlowStyle,
        }}
      />

      {/* Blue glow (Figma 6142:29477) — flipped horizontally. */}
      <span
        aria-hidden="true"
        data-testid="guarantees-glow-blue"
        className="pointer-events-none absolute top-[124.35px] left-[28.4px] h-[89.06px] w-[240.9px] -scale-x-100 lg:top-[113.67px] lg:left-[36.95px]"
        style={blueGlowStyle}
      >
        <span
          className="absolute inset-[-44.9%_-16.6%]"
          style={blueGlowScaleStyle}
        >
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="/images/home/guarantee-glow-blue.svg"
            alt=""
            className="block size-full max-w-none"
          />
        </span>
      </span>

      {/* Frosted glass UI (Figma 6142:29478) — bleeds off the right edge. */}
      <div
        data-testid="guarantees-glass"
        className="absolute top-[115.5px] left-[21px] flex h-[106.74px] w-[577.36px] items-center rounded-[24px] border-[0.74px] border-white/20 px-[12.51px] backdrop-blur-[9px] lg:top-[104.81px] lg:left-[29.54px]"
        style={{
          backgroundImage:
            "linear-gradient(114.107deg, rgba(255,255,255,0.05) 22.267%, rgba(255,255,255,0.1) 81.701%)",
          ...glassStyle,
        }}
      >
        <div className="bg-grey-0 border-grey-100 flex h-[80.76px] w-[509.32px] items-center gap-[19px] overflow-hidden rounded-[12.33px] border pl-[8.6px]">
          <span className="bg-grey-0 flex size-[62.69px] shrink-0 items-center justify-center rounded-[4.11px] shadow-[0px_9.25px_32.89px_0px_rgba(0,0,0,0.16)]">
            <span
              data-testid="guarantees-check"
              className={
                checkShowsOverlay
                  ? "bg-grey-100 relative flex size-[19.86px] items-center justify-center rounded-full"
                  : "bg-pulse-700 flex size-[19.86px] items-center justify-center rounded-full"
              }
              style={checkTileStyle}
            >
              {checkShowsOverlay ? (
                <span
                  data-testid="guarantees-check-pulse"
                  className="bg-pulse-700 absolute inset-0 rounded-full"
                  style={{ opacity: checkProgress }}
                  aria-hidden
                />
              ) : null}
              <Check
                className="relative size-[13px] text-white"
                strokeWidth={3}
              />
            </span>
          </span>
          <span className="text-grey-500 text-[17.82px] leading-[1.4] tracking-[-0.02em] whitespace-nowrap">
            {copy.mockBusinessFundingLabel}
          </span>
        </div>
      </div>
    </>
  );
}

/** "No personal guarantees" (Figma 6142:29472) — a frosted-glass "Business
 * funding" bar (bleeding off the right edge) selecting the blue check, over a
 * faded "No personal liability" row, with vendored blue + gray glow layers. */
export function StaticGuaranteesCard({
  copy = enLiveHomeCopy.terms,
}: {
  copy?: TermsCopy;
}) {
  return (
    // Mobile (Figma 5948:28593): 24px padding; desktop 32px. The absolute
    // layers shift by (-8.5px, +10.7px) on mobile per the 6147 nodes.
    <div className="border-grey-100 bg-grey-0 relative h-[280px] overflow-hidden rounded-2xl border p-6 lg:p-8">
      <CardLede
        lead={copy.guaranteesLead}
        rest={copy.guaranteesRest}
        className="max-w-[240px] lg:max-w-[309px]"
      />

      <GuaranteesMock copy={copy} />
    </div>
  );
}
